import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Leaf,
  LogOut,
  Users,
  Settings as SettingsIcon,
  Upload,
  Database,
  FileSpreadsheet,
  Link,
  Tags,
  Clock,
  DollarSign,
  FileText,
  Syringe,
  BarChart,
  UserPlus,
  Terminal,
  Lightbulb,
  Scale,
  MessageSquare,
  ClipboardList,
  ListChecks,
  CreditCard,
  UserCog,
  ChevronDown,
  ChevronRight,
  Lock,
  Shield,
  Wallet,
  Edit2,
  Trash2,
  AlertCircle,
  Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserModal } from '../components/UserModal';

interface User {
  id: string;
  email: string;
  created_at: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
}

export function Settings() {
  const navigate = useNavigate();
  const { user, userRoles, signOut } = useAuth();
  const isAdmin = userRoles.includes('admin');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    'data-import': true,
    'membership': true,
    'configuration': true,
    'analytics': true,
    'patient-management': true,
    'system': true
  });

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase.rpc('get_users');
      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      return;
    }

    try {
      const { error } = await supabase.rpc('delete_user', {
        user_id: userId
      });

      if (error) throw error;

      setSuccess(`Successfully deleted user ${userEmail}`);
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(`Failed to delete user: ${error.message}`);
    }
  };

  const handleEditUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowUserModal(true);
  };

  const handleAddUser = () => {
    setSelectedUserId(undefined);
    setShowUserModal(true);
  };

  const handleUserModalSuccess = () => {
    fetchUsers();
    setSuccess('User updated successfully');
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group settings by category
  const settingsCategories = {
    'data-import': {
      title: 'Data Import',
      icon: Upload,
      items: [
        {
          title: 'Import Data',
          icon: FileSpreadsheet,
          description: 'Import data from Athena (CSV files)',
          onClick: () => navigate('/import-data'),
          adminOnly: false
        },
        // {
        //   title: 'Import Membership Information',
        //   icon: FileSpreadsheet,
        //   description: 'Import and process membership data',
        //   onClick: () => navigate('/membership-import'),
        //   adminOnly: false
        // },
        {
          title: 'Import Memberships',
          icon: FileSpreadsheet,
          description: 'Import membership report from Aesthetics Pro',
          onClick: () => navigate('/membership-activity-import'),
          adminOnly: false
        },
        {
          title: 'Import Membership Payments',
          icon: CreditCard,
          description: 'Import Invoice line item report from Aesthetics Pro',
          onClick: () => navigate('/membership-payments-import'),
          adminOnly: false
        }
      ]
    },
    'membership': {
      title: 'Membership Management',
      icon: CreditCard,
      items: [
        {
          title: 'Membership Mapping',
          icon: Link,
          description: 'Map membership records to patients',
          onClick: () => navigate('/membership-mapping'),
          adminOnly: false
        },
        {
          title: 'Membership Income',
          icon: Wallet,
          description: 'View and analyze membership income',
          onClick: () => navigate('/membership-income'),
          adminOnly: true
        }
      ]
    },
    'configuration': {
      title: 'System Configuration',
      icon: SettingsIcon,
      items: [
        {
          title: 'Product Categories',
          icon: Tags,
          description: 'Manage product sale categories',
          onClick: () => navigate('/product-categories'),
          adminOnly: true
        },
        {
          title: 'Procedure Categories',
          icon: FileText,
          description: 'Map procedure codes to categories',
          onClick: () => navigate('/procedure-categories'),
          adminOnly: true
        },
        {
          title: 'Service Levels',
          icon: Clock,
          description: 'Manage service level types',
          onClick: () => navigate('/service-levels'),
          adminOnly: true
        },
        {
          title: 'Procedure Pricing',
          icon: DollarSign,
          description: 'Set pricing for procedure codes',
          onClick: () => navigate('/procedure-pricing'),
          adminOnly: true
        },
        {
          title: 'Procedure Codes',
          icon: FileText,
          description: 'Update codes and medication details',
          onClick: () => navigate('/procedure-codes'),
          adminOnly: false
        },
        {
          title: 'Administered Medications Mapping',
          icon: Syringe,
          description: 'Map administered medications to procedure codes',
          onClick: () => navigate('/administered-medications-mapping'),
          adminOnly: true
        },
        {
          title: 'Intake Workflow Steps',
          icon: ClipboardList,
          description: 'Configure lead intake workflow steps',
          onClick: () => navigate('/intake-workflow-steps'),
          adminOnly: true
        },
        {
          title: 'Lead Status Management',
          icon: ListChecks,
          description: 'Manage patient lead status options',
          onClick: () => navigate('/lead-status-management'),
          adminOnly: true
        }
      ]
    },
    'analytics': {
      title: 'Analytics & Reporting',
      icon: BarChart,
      items: [
        {
          title: 'Data Analytics',
          icon: BarChart,
          description: 'View data analytics and reports',
          onClick: () => navigate('/data-analytics'),
          adminOnly: true
        },
        {
          title: 'Weight Loss Data',
          icon: Scale,
          description: 'Analyze patient weight loss metrics',
          onClick: () => navigate('/weight-loss-data'),
          adminOnly: true
        },
        {
          title: 'Provider Visits',
          icon: UserCog,
          description: 'View provider visit statistics',
          onClick: () => navigate('/provider-visits'),
          adminOnly: true
        },
        {
          title: 'Membership Payments',
          icon: DollarSign,
          description: 'Review and analyze payment history for memberships',
          onClick: () => navigate('/membership-payments'),
          adminOnly: true
        },
        {
          title: 'Membership Bucket Manager',
          icon: DollarSign,
          description: 'Define Membership Buckets',
          onClick: () => navigate('/MembershipBucketManager'),
          adminOnly: true
        }
      ]
    },
    'patient-management': {
      title: 'Patient Management',
      icon: UserPlus,
      items: [
        {
          title: 'Population Management',
          icon: Users,
          description: 'Manage patient population data',
          onClick: () => navigate('/population-management'),
          adminOnly: true
        },
        {
          title: 'Intake Progress',
          icon: ClipboardList,
          description: 'View and manage intake progress',
          onClick: () => navigate('/intake-progress'),
          adminOnly: true
        },
        {
          title: 'Message Board',
          icon: MessageSquare,
          description: 'View and manage system messages',
          onClick: () => navigate('/message-board'),
          adminOnly: false
        },
        {
          title: 'Enhancement Management',
          icon: Lightbulb,
          description: 'Review and manage enhancement suggestions',
          onClick: () => navigate('/enhancement-management'),
          adminOnly: true
        }
      ]
    },
    'system': {
      title: 'System Administration',
      icon: Shield,
      items: [
        {
          title: 'Security',
          icon: Lock,
          description: 'Review login activity',
          onClick: () => navigate('/login-activity'),
          adminOnly: true
        },
        {
          title: 'SQL Interface',
          icon: Terminal,
          description: 'Execute SQL queries and commands',
          onClick: () => navigate('/sql-interface'),
          adminOnly: true
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">Settings</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">{user?.email}</span>
              <div className="flex flex-wrap gap-2">
                {userRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {role}
                  </span>
                ))}
              </div>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          {/* Header Section */}
          <div className="bg-white shadow-lg rounded-lg mb-8 overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              
              <div className="relative px-8 py-12">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-white hover:text-green-100 transition-colors duration-200"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                  <Leaf className="h-12 w-12 text-white opacity-20" />
                </div>
                
                <div className="mt-4 text-center">
                  <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                  <p className="text-green-100 text-lg mb-4">
                    Tracking Health and Reducing Illness Via Efficiency
                  </p>
                  <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                  <h2 className="text-2xl font-semibold text-white mt-4">Settings</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Categories */}
          <div className="space-y-6">
            {Object.entries(settingsCategories).map(([key, category]) => {
              // Filter items based on admin status
              const items = category.items.filter(item => !item.adminOnly || isAdmin);
              
              // Skip empty categories
              if (items.length === 0) return null;
              
              const CategoryIcon = category.icon;
              const isExpanded = expandedCategories[key];
              
              return (
                <div key={key} className="bg-white shadow-lg rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <button 
                    className="w-full px-6 py-5 flex items-center justify-between bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors duration-150"
                    onClick={() => toggleCategory(key)}
                  >
                    <div className="flex items-center">
                      <CategoryIcon className="h-5 w-5 text-green-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {category.title}
                      </h3>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  
                  {/* Category Items */}
                  {isExpanded && (
                    <div className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((item) => (
                        <div
                          key={item.title}
                          className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200 hover:shadow-md hover:border-green-200 transition-all duration-200 cursor-pointer"
                          onClick={item.onClick}
                        >
                          <div className="p-5">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 bg-green-50 p-2 rounded-lg">
                                <item.icon className="h-5 w-5 text-green-600" />
                              </div>
                              <div className="ml-4">
                                <h4 className="text-base font-medium text-gray-900">
                                  {item.title}
                                </h4>
                                <p className="mt-1 text-sm text-gray-500">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* User Management Section - Only visible to admins */}
          {isAdmin && (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mt-6">
              <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-green-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    User Management
                  </h3>
                </div>
                <button
                  onClick={handleAddUser}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add User
                </button>
              </div>

              {/* Messages */}
              {error && (
                <div className="px-6 py-4 bg-red-50">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <p className="ml-3 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {success && (
                <div className="px-6 py-4 bg-green-50">
                  <div className="flex items-center">
                    <Check className="h-5 w-5 text-green-400" />
                    <p className="ml-3 text-sm text-green-700">{success}</p>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                          Loading users...
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                            <button
                              onClick={() => handleEditUser(user.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          userId={selectedUserId}
          onClose={() => setShowUserModal(false)}
          onSuccess={handleUserModalSuccess}
        />
      )}
    </div>
  );
}